import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { PlaidLinkAccountMetadata } from "@/lib/plaid-link";

interface PlaidAccountSelectDialogProps {
  open: boolean;
  institutionName: string;
  accounts: PlaidLinkAccountMetadata[];
  submitting: boolean;
  error: string | null;
  onConfirm: (selectedIds: string[]) => void;
  onCancel: () => void;
}

// In-app Account Select step rendered between Plaid Link's onSuccess and the
// /exchange call. Plaid Link's hosted Account Select UI only shows when the
// operator has created a Dashboard Link Customization with Account Select
// enabled and pushed PLAID_LINK_CUSTOMIZATION_NAME as a Worker secret; until
// they do, Link silently connects every account in the institution. This
// dialog gives the user the same toggle ability inside our UI regardless of
// the Dashboard state, satisfying FIN-38's acceptance criteria.
export function PlaidAccountSelectDialog({
  open,
  institutionName,
  accounts,
  submitting,
  error,
  onConfirm,
  onCancel,
}: PlaidAccountSelectDialogProps) {
  const accountIds = useMemo(() => accounts.map((a) => a.id), [accounts]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(accountIds));

  // Reset the selection to "all" whenever the dialog opens with a new set of
  // accounts (e.g. user cancels, links again, and gets a different
  // institution). Without this the second Link round would carry over the
  // previous selection, which is confusing.
  useEffect(() => {
    if (open) setSelected(new Set(accountIds));
  }, [open, accountIds]);

  const toggle = (id: string) => {
    if (submitting) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    if (submitting || selected.size === 0) return;
    onConfirm([...selected]);
  };

  const empty = selected.size === 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        if (!next) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Choose accounts to connect</DialogTitle>
          <DialogDescription>
            Select which accounts from{" "}
            <span className="font-medium text-foreground">{institutionName}</span>{" "}
            you want to sync. Only the accounts you keep selected will be linked.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-72 space-y-1 overflow-y-auto rounded-md border border-border">
          {accounts.map((account) => {
            const checked = selected.has(account.id);
            return (
              <label
                key={account.id}
                className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-muted/60"
              >
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-input"
                  checked={checked}
                  onChange={() => toggle(account.id)}
                  disabled={submitting}
                  aria-label={`Connect ${account.name}${account.mask ? ` ending in ${account.mask}` : ""}`}
                />
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="truncate text-sm font-medium text-foreground">
                    {account.name}
                    {account.mask ? (
                      <span className="ml-1.5 font-mono text-xs text-muted-foreground">
                        ••{account.mask}
                      </span>
                    ) : null}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatAccountType(account.type, account.subtype)}
                  </p>
                </div>
              </label>
            );
          })}
        </div>

        {empty ? (
          <p
            role="alert"
            className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning"
          >
            Select at least one account to continue.
          </p>
        ) : null}

        {error ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || empty}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Connecting…
              </>
            ) : (
              `Connect ${selected.size} ${selected.size === 1 ? "account" : "accounts"}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatAccountType(type: string, subtype: string | null): string {
  const t = capitalize(type);
  if (!subtype) return t;
  return `${t} · ${capitalize(subtype)}`;
}

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
